import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import { getCameraList, createCamera, deleteCamera } from '../../services/cameraService';
import AddCameraDialog from '../../components/forms/AddCameraDialog';
import EditCameraDialog from '../../components/forms/Edit_forms/EditCameraDialog';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';

function flattenErrors(obj, prefix = '') {
  let messages = [];
  for (const [key, value] of Object.entries(obj)) {
    const label = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      messages.push(`${label}: ${value.join(', ')}`);
    } else if (typeof value === 'object' && value !== null) {
      messages = messages.concat(flattenErrors(value, label));
    } else {
      messages.push(`${label}: ${value}`);
    }
  }
  return messages;
}

const CameraPage = () => {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { user } = useAuth();

  const fetchCameras = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCameraList();
      console.log('Camera API response:', data); // Debug API response
      if (Array.isArray(data)) {
        setCameras(data);
      } else if (data.results) {
        setCameras(data.results);
      } else {
        setCameras([]);
      }
    } catch (e) {
      console.error('Error fetching cameras:', e);
      setError('Failed to load camera list.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCameras();
  }, []);

  const handleMenuOpen = (event, camera) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedCamera(camera);
  };

  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleEditClick = () => {
    setEditOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = async () => {
    handleMenuClose();
    if (!selectedCamera) return;

    const confirmed = window.confirm(`Delete camera ID "${selectedCamera.id}"?`);
    if (!confirmed) return;

    try {
      await deleteCamera(selectedCamera.id);
      setSnackbar({ open: true, message: 'Camera deleted.', severity: 'success' });
      fetchCameras();
    } catch (err) {
      console.error('Delete camera error:', err);
      setSnackbar({ open: true, message: 'Failed to delete camera.', severity: 'error' });
    }
  };

  const handleAddCamera = async (form) => {
    try {
      // Only send location, not id
      await createCamera({ location: form.location });
      setSnackbar({ open: true, message: 'Camera added successfully!', severity: 'success' });
      setAddOpen(false);
      fetchCameras();
    } catch (err) {
      console.error('Add camera error details:', err.response?.data || err.message);
      let message = 'Failed to add camera';
      if (err.response && err.response.data) {
        const data = err.response.data;
        if (typeof data === 'string') message = data;
        else if (typeof data === 'object') message = flattenErrors(data).join(' | ');
      }
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Cameras
      </Typography>
      <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setAddOpen(true)}>
        Add Camera
      </Button>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Location</TableCell>
                  {user?.role === 'admin' && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {(cameras || []).map((camera) => (
                  <TableRow key={camera.id}>
                    <TableCell>{camera.id}</TableCell>
                    <TableCell>{camera.location}</TableCell>
                    {user?.role === 'admin' && (
                      <TableCell>
                        <IconButton onClick={(e) => handleMenuOpen(e, camera)}>
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <EditCameraDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onEdit={fetchCameras}
        initialData={selectedCamera}
      />

      {/* Updated: pass only location to onAdd */}
      <AddCameraDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={(form) => handleAddCamera({ location: form.location })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
};

export default CameraPage;
